import { useState } from "react";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";

export interface AccessControlEntity {
    excludedUsers: Array<string>;
    additionalAccessUsers: Array<string>;
    visibleToAllUsers: boolean;
    accessBusinessAreas: Array<string>;
    responsibleId?: string | null;
    allGroupEntityAccess: boolean;
    groupEntities: Array<string>;
}

export function useAccessResponsibilityHelper<T extends AccessControlEntity>(
    onChange: (changeFunc: (entity: T) => void) => Promise<void>,
    onConfirmNavigate: () => void,
    disableAccessWarning?: boolean
) {
    const { businessAreas: userBusinessAreas, userProfileId } = useUserContext();
    const [pendingUserChanges, setPendingUserChanges] = useState<T | null>(null);

    async function handleDialogConfirm() {
        await onChange((entity: T) => {
            Object.assign(entity, pendingUserChanges!);
        });

        setPendingUserChanges(null);
        onConfirmNavigate();
    }

    const onFieldChange = (newEntity: T, oldEntity: T) => {
        if (!disableAccessWarning && isRemovingOwnAccess(newEntity, oldEntity, userProfileId, userBusinessAreas)) {
            setPendingUserChanges(newEntity);
        } else {
            onChange((entity: T) => {
                Object.assign(entity, newEntity);
            });
        }
    };

    return { onFieldChange, handleDialogConfirm, pendingUserChanges, setPendingUserChanges };
}

export function isRemovingOwnAccess<T extends AccessControlEntity>(newEntity: T, oldEntity: T, userProfileId: string, userBusinessAreas: Array<string>) {
    const removedFromEntity = isBeingRemovedFromEntity(newEntity, oldEntity, userProfileId, userBusinessAreas);
    const stillHasAccess = remainsHavingAccess(newEntity, userProfileId, userBusinessAreas);
    return removedFromEntity && !stillHasAccess;
}

function isBeingRemovedFromEntity<T extends AccessControlEntity>(newEntity: T, oldEntity: T, userProfileId: string, userBusinessAreas: Array<string>) {
    if (newEntity.excludedUsers.includes(userProfileId)) {
        return true;
    }

    if (oldEntity.additionalAccessUsers?.includes(userProfileId) && !newEntity.additionalAccessUsers?.includes(userProfileId)) {
        return true;
    }

    if (oldEntity.responsibleId === userProfileId && newEntity.responsibleId !== userProfileId) {
        return true;
    }

    // Without group entity access neither the business area nor the visible-to-all route grants access,
    // so losing it removes access on its own even when both of those still match.
    if (hasGroupEntityAccess(oldEntity) && !hasGroupEntityAccess(newEntity)) {
        return true;
    }

    if (!userBusinessAreas.some((x) => newEntity.accessBusinessAreas.includes(x))) {
        return true;
    }

    return !newEntity.visibleToAllUsers;
}

function remainsHavingAccess<T extends AccessControlEntity>(newEntity: T, userProfileId: string, userBusinessAreas: Array<string>) {
    if (newEntity.responsibleId === userProfileId) {
        return true;
    }

    if (newEntity.excludedUsers?.includes(userProfileId)) {
        return false;
    }

    if (newEntity.additionalAccessUsers?.includes(userProfileId)) {
        return true;
    }

    const hasBusinessAreaAccess = userBusinessAreas.some((x) => newEntity.accessBusinessAreas.includes(x));
    return (newEntity.visibleToAllUsers || hasBusinessAreaAccess) && hasGroupEntityAccess(newEntity);
}

function hasGroupEntityAccess<T extends AccessControlEntity>(entity: T) {
    return entity.allGroupEntityAccess || entity.groupEntities.length > 0;
}
